import { Connection } from 'reactflow';

import { ConnectedLabwareNode } from './nodes/LabwareNode';
import { ConnectedTaskNode } from './nodes/TaskNode/StoreConnector';
import { TimeConstraintNode } from './nodes/TimeConstraintNode';
import {
  DATA_HANDLE_IDENTIFIER,
  SOURCE_HANDLE_IDENTIFIER,
  TARGET_HANDLE_IDENTIFIER,
  TIME_CONSTRAINT_HANDLE_END,
  TIME_CONSTRAINT_HANDLE_START,
} from '../../constants';
import type {
  NonNullishConnection,
  TimeConstraintHandleType,
  TimeConstraintInstance,
} from '../../types';
import { parseHandlePairType } from '../../utils';

export const resolveConnection = (connection: NonNullishConnection) => {
  let resolvedConn = connection;
  if (connection.sourceHandle?.includes(TARGET_HANDLE_IDENTIFIER)) {
    resolvedConn = {
      source: connection.target,
      target: connection.source,
      sourceHandle: connection.targetHandle,
      targetHandle: connection.sourceHandle,
    };
  }
  return resolvedConn;
};

export const nodeTypes = {
  labware: ConnectedLabwareNode,
  task: ConnectedTaskNode,
  timeConstraint: TimeConstraintNode,
};

export enum ConnectionType {
  TIME_CONSTRAINT = 'TimeConstraint',
  DATA_DEPENDENCY = 'DataDependency',
  LABWARE_DEPENDENCY = 'LabwareDependency',
}

const isTimeConstraintConnection = (
  connection: NonNullishConnection
): connection is NonNullishConnection & {
  sourceHandle: TimeConstraintHandleType;
  targetHandle: TimeConstraintHandleType;
} =>
  (connection.sourceHandle === TIME_CONSTRAINT_HANDLE_START ||
    connection.sourceHandle === TIME_CONSTRAINT_HANDLE_END) &&
  (connection.targetHandle === TIME_CONSTRAINT_HANDLE_START ||
    connection.targetHandle === TIME_CONSTRAINT_HANDLE_END);

const isTimeConstraintHandle = (
  handle: Connection['sourceHandle' | 'targetHandle'] | null
) => {
  return (
    handle &&
    [TIME_CONSTRAINT_HANDLE_START, TIME_CONSTRAINT_HANDLE_END].includes(handle)
  );
};

const isDataHandle = (
  handle: Connection['sourceHandle' | 'targetHandle'] | null
) => handle && handle.includes(DATA_HANDLE_IDENTIFIER);

const isLabwareDependencyConnection = (
  connection: NonNullishConnection
): boolean => {
  if (
    isTimeConstraintHandle(connection.sourceHandle) ||
    isTimeConstraintHandle(connection.targetHandle) ||
    isDataHandle(connection.sourceHandle) ||
    isDataHandle(connection.targetHandle)
  ) {
    return false;
  }

  const { source, target } = abstractHandlersDirection(connection);

  const sourceHandleIsValid =
    source === null || parseHandlePairType(source) === SOURCE_HANDLE_IDENTIFIER;

  const targetHandleIsValid =
    target !== null && parseHandlePairType(target) === TARGET_HANDLE_IDENTIFIER;

  return sourceHandleIsValid && targetHandleIsValid;
};

const abstractHandlersDirection = (connection: Connection) => {
  let source = connection.sourceHandle;
  let target = connection.targetHandle;

  // reverse if needed
  if (
    connection.sourceHandle !== null &&
    parseHandlePairType(connection.sourceHandle) === TARGET_HANDLE_IDENTIFIER
  ) {
    source = connection.targetHandle;
    target = connection.sourceHandle;
  }

  return { source, target };
};

const isDataConnection = ({
  sourceHandle,
  targetHandle,
}: NonNullishConnection) =>
  isDataHandle(sourceHandle) && isDataHandle(targetHandle);

type NarrowedConnection =
  | { type: ConnectionType.DATA_DEPENDENCY; connection: NonNullishConnection }
  | {
      type: ConnectionType.LABWARE_DEPENDENCY;
      connection: NonNullishConnection;
    }
  | {
      type: ConnectionType.TIME_CONSTRAINT;
      connection: NonNullishConnection & {
        sourceHandle: TimeConstraintHandleType;
        targetHandle: TimeConstraintHandleType;
      };
    }
  | { type: null; connection: null };

export const getConnectionType = (
  connection: NonNullishConnection
): NarrowedConnection => {
  if (isTimeConstraintConnection(connection)) {
    return { type: ConnectionType.TIME_CONSTRAINT, connection };
  }

  if (isLabwareDependencyConnection(connection)) {
    return {
      type: ConnectionType.LABWARE_DEPENDENCY,
      connection: resolveConnection(connection),
    };
  }

  if (isDataConnection(connection)) {
    return {
      type: ConnectionType.DATA_DEPENDENCY,
      connection: resolveConnection(connection),
    };
  }

  return { type: null, connection: null };
};

export const getTimeConstraint = (
  connection: NonNullishConnection & {
    sourceHandle: TimeConstraintHandleType;
    targetHandle: TimeConstraintHandleType;
  }
): Omit<TimeConstraintInstance, 'id' | 'minDuration' | 'maxDuration'> => {
  return {
    source: connection.source,
    sourceType: connection.sourceHandle,
    target: connection.target,
    targetType: connection.targetHandle,
    enableDetailedCoverageCalculation: true,
    autoIgnoreIfBroken: false,
  };
};

export const isConnectingToItself = (connection: Connection) =>
  connection.source === connection.target;

export const isNotNullConnection = (
  connection: Connection
): connection is NonNullishConnection =>
  connection.source !== null &&
  connection.target !== null &&
  connection.sourceHandle !== null &&
  connection.targetHandle !== null;

export const validateConnection = (
  connection: Connection
): null | NonNullishConnection => {
  if (!isNotNullConnection(connection)) {
    return null;
  }

  return {
    source: connection.source,
    target: connection.target,
    sourceHandle: connection.sourceHandle,
    targetHandle: connection.targetHandle,
  };
};
